<script lang="ts">
  export type TooltipHintProps = {
    text?: string
    /** Class for the tooltip */
    class?: HTMLAttributes['class']
    /** Class for the icon */
    iconClass?: HTMLAttributes['class']
    maxWidth?: number
    // Part of the data-shape for hint configs (e.g. support-layout.vue passes `{ text, icon }` objects). Not rendered by tooltip-hint.vue — wiring is a known follow-up.
    // eslint-disable-next-line vue/no-unused-properties
    icon?: IconComponent
    disabled?: boolean
  }
</script>

<script setup lang="ts">
  import {
    Tooltip,
    TooltipTrigger,
    TooltipContent,
    TooltipProvider
  } from '../ui/tooltip'
  import { type HTMLAttributes } from 'vue'
  import type { IconComponent } from '@gat-ship/icons'
  import { cn } from '../../lib/utils'
  const props = withDefaults(defineProps<TooltipHintProps>(), {
    maxWidth: 260,
    disabled: false
  })

  function getMaxWidth() {
    return props.maxWidth ? props.maxWidth + 'px' : 'auto'
  }
</script>

<template>
  <TooltipProvider :disabled="disabled">
    <Tooltip>
      <TooltipTrigger as-child>
        <slot>
          <div v-if="!disabled" class="size-fit rounded-full">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 20 20"
              fill="currentColor"
              :class="
                cn(
                  'size-4 text-neutral-400 dark:text-neutral-600',
                  props.iconClass
                )
              "
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0ZM8.94 6.94a.75.75 0 1 1-1.061-1.061 3 3 0 1 1 2.871 5.026v.345a.75.75 0 0 1-1.5 0v-.5c0-.72.57-1.172 1.081-1.287A1.5 1.5 0 1 0 8.94 6.94ZM10 15a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
        </slot>
      </TooltipTrigger>
      <TooltipContent
        :arrow-padding="0"
        :arrow-class="cn('bg-black/90 fill-black/90')"
        :class="cn('bg-black/90 text-xs text-white', props.class)"
        as-child
      >
        <div
          :class="cn('flex w-fit whitespace-pre-line', props.class)"
          :style="{ maxWidth: getMaxWidth() }"
        >
          <slot name="content">
            {{ text }}
          </slot>
        </div>
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
</template>
